import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { axiosInstance } from "../App";
import { ArrowLeft, Mail, User as UserIcon, Bell, Loader2, Lock, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from 'react-i18next';
import BillingSubscription from "../components/BillingSubscription";

function Settings() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [notificationSettings, setNotificationSettings] = useState({
    task_assigned: true,
    comments: true,
    task_status_changed: true,
    member_added: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);

  useEffect(() => {
    fetchUserData();
    fetchPreferences();
  }, []);

  const fetchUserData = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem("user") || "{}");
      setUser(userData);
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const fetchPreferences = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get("/auth/preferences");
      if (response.data.email_notification_settings) {
        setNotificationSettings(response.data.email_notification_settings);
      }
    } catch (error) {
      toast.error(t('settingsLoadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationChange = async (key, value) => {
    const newSettings = { ...notificationSettings, [key]: value };
    setNotificationSettings(newSettings);

    setSaving(true);
    try {
      await axiosInstance.put("/auth/preferences", {
        email_notification_settings: newSettings
      });
      toast.success(t('settingsSaved'));
    } catch (error) {
      toast.error(t('settingsSaveFailed'));
      // Revert on error
      setNotificationSettings(notificationSettings);
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error("Şifreler eşleşmiyor");
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      toast.error("Yeni şifre en az 6 karakter olmalı");
      return;
    }

    setChangingPassword(true);
    try {
      await axiosInstance.post("/auth/change-password", {
        old_password: passwordForm.oldPassword,
        new_password: passwordForm.newPassword
      });
      toast.success("Şifre başarıyla değiştirildi");
      setPasswordForm({ oldPassword: "", newPassword: "", confirmPassword: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Şifre değiştirilemedi");
    } finally {
      setChangingPassword(false);
    }
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setProfilePicture(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePicturePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadProfilePicture = async () => {
    if (!profilePicture) return;

    const formData = new FormData();
    formData.append('file', profilePicture);

    setUploadingPicture(true);
    try {
      const response = await axiosInstance.post("/auth/upload-profile-picture", formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Update local storage
      const updatedUser = { ...user, profile_picture_url: response.data.profile_picture_url };
      localStorage.setItem("user", JSON.stringify(updatedUser));
      setUser(updatedUser);
      
      toast.success("Profil fotoğrafı güncellendi");
      setProfilePicture(null);
      setProfilePicturePreview(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fotoğraf yüklenemedi");
    } finally {
      setUploadingPicture(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto p-6">
        <div className="mb-6">
          <div className="flex items-center gap-4 mb-4">
            <Button variant="ghost" onClick={() => navigate("/dashboard")} data-testid="back-button">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('back')}
            </Button>
          </div>
          <h1 className="text-3xl font-bold">{t('settings')}</h1>
        </div>

        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="profile">
              <UserIcon className="h-4 w-4 mr-2" />
              {t('profile')}
            </TabsTrigger>
            <TabsTrigger value="notifications">
              <Bell className="h-4 w-4 mr-2" />
              {t('notifications')}
            </TabsTrigger>
            <TabsTrigger value="billing">
              <CreditCard className="h-4 w-4 mr-2" />
              {t('billing.title')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="space-y-6">
            {/* Profile Section */}
            <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserIcon className="h-5 w-5" />
                {t('profile')}
              </CardTitle>
              <CardDescription>{t('profileDescription')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-6">
                <Avatar className="h-24 w-24">
                  <AvatarImage src={profilePicturePreview || user?.profile_picture_url} />
                  <AvatarFallback className="text-2xl">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold">{user?.name}</h3>
                  <p className="text-gray-500 flex items-center gap-2 mt-1">
                    <Mail className="h-4 w-4" />
                    {user?.email}
                  </p>
                  <div className="mt-4 space-y-2">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleProfilePictureChange}
                      className="max-w-xs"
                    />
                    {profilePicture && (
                      <Button
                        onClick={uploadProfilePicture}
                        disabled={uploadingPicture}
                        size="sm"
                      >
                        {uploadingPicture ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Yükleniyor...
                          </>
                        ) : (
                          "Profil Fotoğrafını Güncelle"
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Password Change Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5" />
                Şifre Değiştir
              </CardTitle>
              <CardDescription>Hesap güvenliğiniz için şifrenizi güncelleyin</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <Label htmlFor="oldPassword">Mevcut Şifre</Label>
                  <Input
                    id="oldPassword"
                    type="password"
                    value={passwordForm.oldPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, oldPassword: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="newPassword">Yeni Şifre</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                    required
                    minLength={6}
                  />
                </div>
                <div>
                  <Label htmlFor="confirmPassword">Yeni Şifre (Tekrar)</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                    required
                    minLength={6}
                  />
                </div>
                <Button
                  type="submit"
                  disabled={changingPassword}
                >
                  {changingPassword ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Değiştiriliyor...
                    </>
                  ) : (
                    "Şifreyi Değiştir"
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
          </TabsContent>

          <TabsContent value="notifications" className="space-y-6">
            {/* Notifications Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-5 w-5" />
                  {t('notifications')}
                </CardTitle>
                <CardDescription>{t('notificationsDescription')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="task-assigned">{t('taskAssigned')}</Label>
                      <p className="text-sm text-gray-500">{t('taskAssignedDescription')}</p>
                    </div>
                    <Switch
                      id="task-assigned"
                      checked={notificationSettings.task_assigned}
                      onCheckedChange={(checked) => handleNotificationChange('task_assigned', checked)}
                      disabled={saving}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="comments">{t('newComment')}</Label>
                      <p className="text-sm text-gray-500">{t('newCommentDescription')}</p>
                    </div>
                    <Switch
                      id="comments"
                      checked={notificationSettings.comments}
                      onCheckedChange={(checked) => handleNotificationChange('comments', checked)}
                      disabled={saving}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="task-status">{t('taskStatusChanged')}</Label>
                      <p className="text-sm text-gray-500">{t('taskStatusChangedDescription')}</p>
                    </div>
                    <Switch
                      id="task-status"
                      checked={notificationSettings.task_status_changed}
                      onCheckedChange={(checked) => handleNotificationChange('task_status_changed', checked)}
                      disabled={saving}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="member-added">{t('memberAdded')}</Label>
                      <p className="text-sm text-gray-500">{t('memberAddedDescription')}</p>
                    </div>
                    <Switch
                      id="member-added"
                      checked={notificationSettings.member_added}
                      onCheckedChange={(checked) => handleNotificationChange('member_added', checked)}
                      disabled={saving}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="billing" className="space-y-6">
            <BillingSubscription />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

export default Settings;
